import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';
import { jukeboxRepository } from '../../data/jukeboxRepository';
import { compararPorMaisPedida } from '../../domain/usecases/ordenarRepertorio';
import { mesclarItensAposFetch } from '../../domain/usecases/mesclarItensAposFetch';
import type { PlaylistItem } from '@/shared/models/playlist.model';

/**
 * usePlaylistItens — sincroniza os itens da playlist vinculada à tela.
 * Canal `playlist-itens-{playlistId}` (INSERT/UPDATE/DELETE); DELETE também
 * limpa a fila de pedidos por vídeos que saíram da playlist.
 */
export interface PlaylistItensState {
  items: PlaylistItem[];
  setItems: React.Dispatch<React.SetStateAction<PlaylistItem[]>>;
  handleReloadPlaylist(): Promise<void>;
  criarCanalPlaylist(): void;
  destruirCanalPlaylist(): void;
  /** Abre a janela de UM fetch de repertório e devolve o token dela. */
  iniciarJanelaItens(): number;
  /** Mescla o snapshot com o que o CDC aplicou durante a janela `token` e a fecha. */
  aplicarSnapshotItens(snapshot: PlaylistItem[], token: number): void;
  /** Fecha a janela sem aplicar (fetch falhou). */
  cancelarJanelaItens(token: number): void;
}

interface JanelaFetch {
  removidos: Set<string>;
  inseridos: Set<string>;
}

export function usePlaylistItens(
  telaId: string | undefined,
  playlistId: string | null | undefined,
): PlaylistItensState {
  const [items, setItems] = useState<PlaylistItem[]>([]);

  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  // Mesmo motivo de useComandoChannel: channel() do mesmo tópico em `leaving`
  // devolve a instância que o close vai derrubar.
  const remocaoRef = useRef<Promise<unknown>>(Promise.resolve());
  const remocaoProntaRef = useRef(true);
  const geracaoRef = useRef(0);
  // Uma janela por fetch em andamento — mesmo motivo de `useFilaCooldown`:
  // um segundo fetch não pode fechar a janela do primeiro.
  const janelasRef = useRef<Map<number, JanelaFetch>>(new Map());
  const proximoTokenRef = useRef(0);
  const jaSubscreveuRef = useRef(false);

  const registrarRemocao = (id: string) => {
    for (const janela of janelasRef.current.values()) janela.removidos.add(id);
  };

  const registrarInsercao = (id: string) => {
    for (const janela of janelasRef.current.values()) janela.inseridos.add(id);
  };

  const iniciarJanelaItens = useCallback(() => {
    const token = ++proximoTokenRef.current;
    janelasRef.current.set(token, { removidos: new Set(), inseridos: new Set() });
    return token;
  }, []);

  const cancelarJanelaItens = useCallback((token: number) => {
    janelasRef.current.delete(token);
  }, []);

  const aplicarSnapshotItens = useCallback((snapshot: PlaylistItem[], token: number) => {
    // Captura e fecha a janela fora do updater do setState (o React pode
    // chamar o updater duas vezes em StrictMode).
    const janela = janelasRef.current.get(token);
    janelasRef.current.delete(token);
    const removidos = janela?.removidos ?? new Set<string>();
    const inseridos = janela?.inseridos ?? new Set<string>();
    setItems(prev => mesclarItensAposFetch(prev, snapshot, removidos, inseridos));
  }, []);

  const handleReloadPlaylist = useCallback(async () => {
    if (!playlistId || !telaId) return;
    const token = iniciarJanelaItens();
    try {
      const novosItens = await jukeboxRepository.listarPlaylistItens(playlistId, telaId);
      aplicarSnapshotItens(novosItens, token);
    } catch (error) {
      cancelarJanelaItens(token);
      console.error('[usePlaylistItens] Erro ao recarregar playlist:', error);
    }
  }, [playlistId, telaId, iniciarJanelaItens, aplicarSnapshotItens, cancelarJanelaItens]);

  const destruirCanalPlaylist = useCallback(() => {
    geracaoRef.current++;
    if (channelRef.current) {
      remocaoProntaRef.current = false;
      const pendente = Promise.resolve(supabase.removeChannel(channelRef.current)).catch(() => {});
      remocaoRef.current = pendente;
      void pendente.then(() => {
        if (remocaoRef.current === pendente) remocaoProntaRef.current = true;
      });
      channelRef.current = null;
    }
  }, []);

  const criarCanalPlaylist = useCallback(async () => {
    if (!telaId || !playlistId) return;
    destruirCanalPlaylist();
    const geracao = geracaoRef.current;
    if (!remocaoProntaRef.current) await remocaoRef.current;
    if (geracao !== geracaoRef.current) return;
    jaSubscreveuRef.current = false;

    const channel = supabase
      .channel(`playlist-itens-${playlistId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'playlist_itens', filter: `playlist_id=eq.${playlistId}` },
        ({ new: inserido }) => {
          // Item recém-criado ainda não tem pedidos (pedidos_count vem só
          // da RPC de fetch) — entra no fim do ranking, não perde ordenação.
          const item = inserido as PlaylistItem;
          registrarInsercao(item.id);
          setItems(prev => {
            if (prev.some(i => i.id === item.id)) return prev;
            return [...prev, item].sort(compararPorMaisPedida);
          });
        })
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'playlist_itens', filter: `playlist_id=eq.${playlistId}` },
        ({ old }) => {
          const removido = old as PlaylistItem;
          registrarRemocao(removido.id);
          setItems(prev => prev.filter(i => i.id !== removido.id));
          jukeboxRepository.limparFilaForaDaPlaylist(telaId).catch(() => {});
        })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'playlist_itens', filter: `playlist_id=eq.${playlistId}` },
        ({ new: updated }) => {
          // Payload cru do Postgres não traz pedidos_count (é calculado só
          // na RPC) — preserva o valor já carregado em memória.
          // UPDATE não tira o item da lista; não há tombstone de remoção.
          const item = updated as PlaylistItem;
          setItems(prev => prev
            .map(i => i.id === item.id ? { ...item, pedidos_count: i.pedidos_count } : i)
            .sort(compararPorMaisPedida));
        })
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return;
        // Primeira inscrição: o `init` do composer carrega o repertório.
        // Rejoin silencioso do supabase-js (socket caiu e voltou sem
        // destroy/create) precisa do mesmo catch-up da fila.
        if (!jaSubscreveuRef.current) {
          jaSubscreveuRef.current = true;
          return;
        }
        const token = iniciarJanelaItens();
        jukeboxRepository.listarPlaylistItens(playlistId, telaId)
          .then(itens => aplicarSnapshotItens(itens, token))
          .catch(() => cancelarJanelaItens(token));
      });

    channelRef.current = channel;
  }, [telaId, playlistId, destruirCanalPlaylist, iniciarJanelaItens, aplicarSnapshotItens, cancelarJanelaItens]);

  useEffect(() => {
    void criarCanalPlaylist();
    return destruirCanalPlaylist;
  }, [criarCanalPlaylist, destruirCanalPlaylist]);

  return {
    items, setItems, handleReloadPlaylist, criarCanalPlaylist, destruirCanalPlaylist,
    iniciarJanelaItens, aplicarSnapshotItens, cancelarJanelaItens,
  };
}
